import { Box, useTheme } from "@mui/material";
import { Control, ControllerRenderProps } from "react-hook-form";
import {
  HealthHazardLabels,
  HealthHazardOrder,
  NevadaIFCHazardLabels,
  NevadaIFCHazardOrder,
  OregonHazardLabels,
  OregonHazardOrder,
  PhysicalHazardLabels,
  PhysicalHazardOrder,
} from "util/hazards";

import { ChemicalDetailInputs } from ".";
import { HazardCategorySection } from "./HazardCategorySection";
import { HazardCheckboxSection } from "./HazardCheckboxSection";

type LegacyHazardField = keyof Pick<
  ChemicalDetailInputs,
  "physicalHazards" | "healthHazards" | "oregonHazards" | "ifcHazards"
>;

export type HazardChangeHandler = <F extends LegacyHazardField>(
  e: React.ChangeEvent<HTMLInputElement>,
  checked: boolean,
  field: ControllerRenderProps<ChemicalDetailInputs, F>
) => void;

/**
 * Every hazard input on the chemical form
 */
export function ChemicalHazardSections({
  usesHazardCategories,
  jurisdictions,
  control,
  chemical,
  onHazardChange,
}: {
  usesHazardCategories: boolean;
  jurisdictions: readonly string[];
  control: Control<ChemicalDetailInputs>;
  chemical: ChemicalDetailInputs | undefined;
  onHazardChange: HazardChangeHandler;
}) {
  const theme = useTheme();

  return (
    <>
      {usesHazardCategories ? (
        <Box sx={{ marginTop: theme.spacing(4) }}>
          <HazardCategorySection control={control} />
        </Box>
      ) : (
        <Box sx={{ marginTop: theme.spacing(4) }}>
          <HazardCheckboxSection
            name="physicalHazards"
            label="Physical Hazards"
            order={PhysicalHazardOrder}
            hazardLabels={PhysicalHazardLabels}
            control={control}
            defaultValue={chemical?.physicalHazards}
            onHazardChange={onHazardChange}
          />
        </Box>
      )}

      <Box>
        {!usesHazardCategories && (
          <Box sx={{ marginTop: theme.spacing(2) }}>
            <HazardCheckboxSection
              name="healthHazards"
              label="Health Hazards"
              order={HealthHazardOrder}
              hazardLabels={HealthHazardLabels}
              control={control}
              defaultValue={chemical?.healthHazards}
              onHazardChange={onHazardChange}
            />
          </Box>
        )}

        {jurisdictions.includes("OR") && (
          <Box sx={{ marginTop: theme.spacing(4) }}>
            <HazardCheckboxSection
              name="oregonHazards"
              label="Hazards"
              order={OregonHazardOrder}
              hazardLabels={OregonHazardLabels}
              control={control}
              defaultValue={chemical?.oregonHazards}
              onHazardChange={onHazardChange}
            />
          </Box>
        )}

        {jurisdictions.includes("NV") && (
          <Box sx={{ marginTop: theme.spacing(4) }}>
            <HazardCheckboxSection
              name="ifcHazards"
              label="IFC Hazards"
              order={NevadaIFCHazardOrder}
              hazardLabels={NevadaIFCHazardLabels}
              control={control}
              defaultValue={chemical?.ifcHazards}
              onHazardChange={onHazardChange}
            />
          </Box>
        )}
      </Box>
    </>
  );
}
